import { Injectable, inject, signal } from '@angular/core';
import { FikoYTRequestService } from '@/src/app/shared/services/fikoyt-request.service';
import { turkceDuyarsizRegex } from '@/src/app/shared/utils/regex.utils';

export interface ILookupDepartman { _id: string; ad: string; kod?: string }
export interface ILookupPersonel { _id: string; ad: string; soyad: string; unvan?: string; gorev?: string; sicilNo?: string }
export interface ILookupMusteri { _id: string; firmaAdi: string; yetkili?: string; telefon?: string }
export interface ILookupTedarikci { _id: string; firmaAdi: string; kategori?: string; yetkili?: string }
export interface ILookupUye { _id: string; isim: string; soyIsim: string; resimId?: string; kullaniciAdi?: string }
export interface ILookupMakine { _id: string; makineAdi: string; makineKodu?: string; tip?: string; durum?: string }
export interface ILookupMalzeme { _id: string; malzemeAdi: string; pilesmi?: string; kalinlik?: number; renk?: string }
export interface ILookupSiparis { _id: string; siparisNo: string; musteri?: string; urunAdi?: string; durum?: string }

const DB = 'FikoYT.sistem.DB.hareketler.aggregate';
const DB_UYELER = 'FikoYT.sistem.DB.goUyeler.aggregate';
const UYG_YONETIM = 'FikoYT.sistem.hmi.yonetim';
const UYG_SATIS = 'FikoYT.sistem.hmi.satis';
const UYG_URETIM = 'FikoYT.sistem.hmi.uretim';

function bilesenId(setId: string): string {
  return setId.replace(/^hmi\./, '');
}

function temelPipeline(uygulama: string | string[] | null, setId: string | string[]): any[] {
  const setIds = Array.isArray(setId) ? setId : [setId];
  const bilesenIds = setIds.map(bilesenId);
  const setMatch = { $or: [
    { 'veri.icerikler.ek._secilenSetId': { $in: setIds } },
    { 'veri.icerikler.ek.bilesen': { $in: bilesenIds } },
  ]};
  const baseMatch: any = { silindi: { $ne: true } };
  if (uygulama) {
    const uygulamalar = Array.isArray(uygulama) ? uygulama : [uygulama];
    baseMatch.uygulama = { $in: uygulamalar };
  }
  return [
    { $match: baseMatch },
    { $lookup: { from: 'hareketler', localField: 'veri.icerikler', foreignField: '_id', as: 'veri.icerikler' } },
    { $match: setMatch },
  ];
}

function aramaAdimi(alanlar: string[], arama: string): any {
  const rx = turkceDuyarsizRegex(arama);
  if (!rx) return null;
  if (alanlar.length === 1) {
    return { $match: { 'veri.icerikler.ek.parametreler': { $elemMatch: { ismi: alanlar[0], degeri: { $regex: rx, $options: 'i' } } } } };
  }
  return { $match: { 'veri.icerikler.ek.parametreler': { $elemMatch: { ismi: { $in: alanlar }, degeri: { $regex: rx, $options: 'i' } } } } };
}

function paramDeger(ek: any, ismi: string): string {
  const diziden = ek?.parametreler?.find((p: any) => p.ismi === ismi)?.degeri;
  if (diziden) return diziden;
  return ek?.[ismi] || '';
}

@Injectable({ providedIn: 'root' })
export class HmiLookupService {
  private req = inject(FikoYTRequestService);

  departmanlar = signal<ILookupDepartman[]>([]);
  personeller = signal<ILookupPersonel[]>([]);
  musteriler = signal<ILookupMusteri[]>([]);
  tedarikciler = signal<ILookupTedarikci[]>([]);
  uyeler = signal<ILookupUye[]>([]);
  makineler = signal<ILookupMakine[]>([]);
  malzemeler = signal<ILookupMalzeme[]>([]);
  siparisler = signal<ILookupSiparis[]>([]);

  private _departmanYuklendi = false;
  private _personelYuklendi = false;
  private _musteriYuklendi = false;
  private _tedarikciYuklendi = false;
  private _uyeYuklendi = false;
  private _makineYuklendi = false;
  private _malzemeYuklendi = false;
  private _siparisYuklendi = false;

  async departmanlariYukle(arama?: string, zorla = false): Promise<ILookupDepartman[]> {
    if (this._departmanYuklendi && !arama && !zorla) return this.departmanlar();
    const pipe = [
      ...temelPipeline(null, 'hmi.departman'),
      ...this.aramaPipe(['departmanAdi'], arama),
      { $sort: { _id: -1 } }, { $limit: 50 },
      { $project: { _id: 1, 'veri.icerikler': 1 } },
    ];
    const ham = await this.req.aggregate<any>(DB, pipe);
    const liste = ham.map(d => {
      const ek = d.veri?.icerikler?.[0]?.ek || {};
      return { _id: d._id, ad: paramDeger(ek, 'departmanAdi'), kod: paramDeger(ek, 'departmanKodu') } as ILookupDepartman;
    });
    if (!arama) { this.departmanlar.set(liste); this._departmanYuklendi = true; }
    return liste;
  }

  async personelleriYukle(arama?: string, zorla = false): Promise<ILookupPersonel[]> {
    if (this._personelYuklendi && !arama && !zorla) return this.personeller();
    const pipe = [
      ...temelPipeline(null, 'hmi.personel'),
      ...this.aramaPipe(['ad', 'soyad'], arama),
      { $sort: { _id: -1 } }, { $limit: 50 },
      { $project: { _id: 1, 'veri.icerikler': 1 } },
    ];
    const ham = await this.req.aggregate<any>(DB, pipe);
    const liste = ham.map(d => {
      const ek = d.veri?.icerikler?.[0]?.ek || {};
      return {
        _id: d._id,
        ad: paramDeger(ek, 'ad'),
        soyad: paramDeger(ek, 'soyad'),
        unvan: paramDeger(ek, 'unvan'),
        gorev: paramDeger(ek, 'gorev'),
        sicilNo: paramDeger(ek, 'sicilNo'),
      } as ILookupPersonel;
    });
    if (!arama) { this.personeller.set(liste); this._personelYuklendi = true; }
    return liste;
  }

  async musterileriYukle(arama?: string, zorla = false): Promise<ILookupMusteri[]> {
    if (this._musteriYuklendi && !arama && !zorla) return this.musteriler();
    const pipe = [
      ...temelPipeline(null, 'hmi.musteri'),
      ...this.aramaPipe(['firmaAdi'], arama),
      { $sort: { _id: -1 } }, { $limit: 50 },
      { $project: { _id: 1, 'veri.icerikler': 1 } },
    ];
    const ham = await this.req.aggregate<any>(DB, pipe);
    const liste = ham.map(d => {
      const ek = d.veri?.icerikler?.[0]?.ek || {};
      return {
        _id: d._id,
        firmaAdi: paramDeger(ek, 'firmaAdi'),
        yetkili: paramDeger(ek, 'yetkili'),
        telefon: paramDeger(ek, 'telefon'),
      } as ILookupMusteri;
    });
    if (!arama) { this.musteriler.set(liste); this._musteriYuklendi = true; }
    return liste;
  }

  async tedarikcileriYukle(arama?: string, zorla = false): Promise<ILookupTedarikci[]> {
    if (this._tedarikciYuklendi && !arama && !zorla) return this.tedarikciler();
    const pipe = [
      ...temelPipeline(null, 'hmi.tedarikci'),
      ...this.aramaPipe(['firmaAdi'], arama),
      { $sort: { _id: -1 } }, { $limit: 50 },
      { $project: { _id: 1, 'veri.icerikler': 1 } },
    ];
    const ham = await this.req.aggregate<any>(DB, pipe);
    const liste = ham.map(d => {
      const ek = d.veri?.icerikler?.[0]?.ek || {};
      return {
        _id: d._id,
        firmaAdi: paramDeger(ek, 'firmaAdi'),
        kategori: paramDeger(ek, 'kategori'),
        yetkili: paramDeger(ek, 'yetkili'),
      } as ILookupTedarikci;
    });
    if (!arama) { this.tedarikciler.set(liste); this._tedarikciYuklendi = true; }
    return liste;
  }

  async uyeleriYukle(arama?: string, zorla = false): Promise<ILookupUye[]> {
    if (this._uyeYuklendi && !arama && !zorla) return this.uyeler();
    const match: any = { silindi: { $ne: true } };
    if (arama?.trim()) {
      const rx = turkceDuyarsizRegex(arama.trim());
      if (rx) {
        match.$or = [
          { 'veri.pilesmi': { $regex: rx, $options: 'i' } },
          { kullaniciAdi: { $regex: rx, $options: 'i' } },
        ];
      }
    }
    const pipe = [
      { $match: match },
      { $project: { isim: 1, soyIsim: 1, resimId: { $ifNull: ['$profilResmi', { $ifNull: ['$veri.resimId', ''] }] }, kullaniciAdi: 1 } },
      { $limit: 50 },
    ];
    const liste = (await this.req.aggregate<ILookupUye>(DB_UYELER, pipe));
    if (!arama) { this.uyeler.set(liste); this._uyeYuklendi = true; }
    else { this.uyeler.set(liste); }
    return liste;
  }

  async firmalariYukle(arama?: string): Promise<(ILookupMusteri | ILookupTedarikci)[]> {
    const pipe = [
      ...temelPipeline(null, ['hmi.musteri', 'hmi.tedarikci']),
      ...this.aramaPipe(['firmaAdi'], arama),
      { $sort: { _id: -1 } }, { $limit: 50 },
      { $project: { _id: 1, 'veri.icerikler': 1 } },
    ];
    return (await this.req.aggregate<any>(DB, pipe)).map(d => {
      const ic = d.veri?.icerikler?.[0];
      const ek = ic?.ek || {};
      return { _id: d._id, firmaAdi: paramDeger(ek, 'firmaAdi'), yetkili: paramDeger(ek, 'yetkili'), _setId: ic?.ek?._secilenSetId };
    });
  }

  async makineleriYukle(arama?: string, zorla = false): Promise<ILookupMakine[]> {
    if (this._makineYuklendi && !arama && !zorla) return this.makineler();
    const pipe = [
      ...temelPipeline(null, 'hmi.makine-tanimi'),
      ...this.aramaPipe(['makineAdi', 'makineKodu'], arama),
      { $sort: { _id: -1 } }, { $limit: 50 },
      { $project: { _id: 1, 'veri.icerikler': 1 } },
    ];
    const ham = await this.req.aggregate<any>(DB, pipe);
    const liste = ham.map(d => {
      const ek = d.veri?.icerikler?.[0]?.ek || {};
      return {
        _id: d._id,
        makineAdi: paramDeger(ek, 'makineAdi'),
        makineKodu: paramDeger(ek, 'makineKodu'),
        tip: paramDeger(ek, 'tip'),
        durum: paramDeger(ek, 'durum'),
      } as ILookupMakine;
    });
    if (!arama) { this.makineler.set(liste); this._makineYuklendi = true; }
    else { this.makineler.set(liste); }
    return liste;
  }

  async siparisleriYukle(arama?: string, zorla = false): Promise<ILookupSiparis[]> {
    if (this._siparisYuklendi && !arama && !zorla) return this.siparisler();
    const pipe = [
      ...temelPipeline(null, 'hmi.siparis'),
      ...this.aramaPipe(['siparisNo', 'urunAdi', 'musteri'], arama),
      { $sort: { _id: -1 } }, { $limit: 50 },
      { $project: { _id: 1, 'veri.icerikler': 1 } },
    ];
    const ham = await this.req.aggregate<any>(DB, pipe);
    const liste = ham.map(d => {
      const ek = d.veri?.icerikler?.[0]?.ek || {};
      return {
        _id: d._id,
        siparisNo: paramDeger(ek, 'siparisNo'),
        musteri: paramDeger(ek, 'musteri'),
        urunAdi: paramDeger(ek, 'urunAdi'),
        durum: paramDeger(ek, 'durum'),
      } as ILookupSiparis;
    });
    if (!arama) { this.siparisler.set(liste); this._siparisYuklendi = true; }
    else { this.siparisler.set(liste); }
    return liste;
  }

  async malzemeleriYukle(arama?: string, zorla = false): Promise<ILookupMalzeme[]> {
    if (this._malzemeYuklendi && !arama && !zorla) return this.malzemeler();
    const pipe = [
      ...temelPipeline(null, 'cam.malzeme-tanimi'),
      ...this.aramaPipe(['malzemeAdi'], arama),
      { $sort: { _id: -1 } }, { $limit: 50 },
      { $project: { _id: 1, 'veri.icerikler': 1 } },
    ];
    const ham = await this.req.aggregate<any>(DB, pipe);
    const liste = ham.map(d => {
      const ek = d.veri?.icerikler?.[0]?.ek || {};
      return {
        _id: d._id,
        malzemeAdi: paramDeger(ek, 'malzemeAdi'),
        pilesmi: paramDeger(ek, 'pilesmi'),
        kalinlik: +(paramDeger(ek, 'kalinlik') || 0),
        renk: paramDeger(ek, 'renk'),
      } as ILookupMalzeme;
    });
    if (!arama) { this.malzemeler.set(liste); this._malzemeYuklendi = true; }
    else { this.malzemeler.set(liste); }
    return liste;
  }

  sifirla() {
    this._departmanYuklendi = this._personelYuklendi = this._musteriYuklendi = this._tedarikciYuklendi = this._uyeYuklendi = this._makineYuklendi = this._malzemeYuklendi = this._siparisYuklendi = false;
    this.departmanlar.set([]);
    this.personeller.set([]);
    this.musteriler.set([]);
    this.tedarikciler.set([]);
    this.uyeler.set([]);
    this.makineler.set([]);
    this.malzemeler.set([]);
    this.siparisler.set([]);
  }

  private aramaPipe(alanlar: string[], arama?: string): any[] {
    if (!arama?.trim()) return [];
    const adim = aramaAdimi(alanlar, arama.trim());
    return adim ? [adim] : [];
  }
}
